import {
  ColumnsModel,
  QueryBuilderComponent,
  RuleChangeEventArgs,
  RuleModel,
} from "@syncfusion/ej2-react-querybuilder";
import { QueryBuilderProps } from "../Fields/types/fieldTypes";
import { useCallback, useMemo, useRef, useState } from "react";
import { DataManager, Query } from "@syncfusion/ej2-data";
import { SimpleFilterField } from "./types";
import { makeTemplateField } from "./makeTemplateField";
import {
  EnumCheckboxField,
  EnumCheckboxFieldProps,
  ODataCheckboxField,
  ODataCheckboxFieldProps,
  DateRangeField,
  DateRangeFieldProps,
} from "../Fields";
import { cloneDeep } from "lodash";

export type LimitedQueryBuilderComponentProps = Omit<
  Partial<QueryBuilderComponent>,
  | "rule"
  | "columns"
  | "enableNotCondition"
  | "ruleChange"
  | "dataSource"
  | "key"
  | "onChange"
  | "ref"
>;

export type QueryBuilderColumn = ColumnsModel | SimpleFilterField;

export interface UseQueryBuilderHookProps extends QueryBuilderProps {
  customQueryBuilderComponentProps?: LimitedQueryBuilderComponentProps;
  // Because the query the query builder returns is tied to the data source,
  // this is how we can get the query without hacking it.
  onQueryChange?: (query: Query) => void;
  columns: QueryBuilderColumn[];
  dataSource?: DataManager;
  value?: RuleModel;
}

// Hint: The special value [null] is used to indicate that the value is not set for arrays in SyncFusion QueryBuilder.
// This is a questionable choice they made, but we need to handle it.
export const useQueryBuilder = ({
  onSubmit,
  onQueryChange,
  columns,
  dataSource,
  value,
}: UseQueryBuilderHookProps) => {
  const queryBuilderComponentRef = useRef<QueryBuilderComponent>(null);

  const [
    hasAtLeastOneRuleChangeBeenCalledSinceLastDefaultValueWasSet,
    setHasAtLeastOneRuleChangeBeenCalledSinceLastDefaultValueWasSet,
  ] = useState(false);

  const queryBuilderComponentKey = useMemo(() => {
    return (
      onSubmit?.toString() ||
      "no-onSubmit" + onQueryChange?.toString() ||
      "no-onQueryChange"
    );
  }, [onSubmit, onQueryChange]);

  // todo: add ability to output the string query
  const ruleChange = useCallback(
    (e: RuleChangeEventArgs, isClearingFilters: boolean = false) => {
      onSubmit?.(e.rule);

      if (queryBuilderComponentRef.current && onQueryChange) {
        if (e.rule && e.rule.rules) {
          const cloned = cloneDeep(e.rule);
          cloned.rules = cloned.rules?.filter(
            (rule) =>
              rule.value !== null &&
              rule.value !== undefined &&
              // this is a special convention that SyncFusion uses to indicate that the value is not set for arrays in QueryBuilder.
              !(Array.isArray(rule.value) && rule.value?.[0] === null)
          );

          const query =
            queryBuilderComponentRef.current.getDataManagerQuery(cloned);
          query.queries = query.queries.filter((q) => q.fn !== "onSelect");

          onQueryChange(query);
        } else {
          onQueryChange(new Query());
        }
      }

      setHasAtLeastOneRuleChangeBeenCalledSinceLastDefaultValueWasSet(
        !isClearingFilters
      );
    },
    [
      onSubmit,
      onQueryChange,
      setHasAtLeastOneRuleChangeBeenCalledSinceLastDefaultValueWasSet,
    ]
  );

  // Process columns to automatically apply appropriate field templates
  const queryBuilderColumns = useMemo(
    () =>
      columns.map((column) => {
        // Handle date columns with "between" operator using DateRangeField
        if (
          "inputType" in column &&
          column.inputType === "date" &&
          column.operator === "between"
        ) {
          return {
            ...column,
            template: makeTemplateField(
              DateRangeField,
              queryBuilderComponentRef,
              column.field,
              {
                label: column.label,
                placeholder: `Select ${column.label?.toLowerCase()} range...`,
              } as DateRangeFieldProps
            ),
          };
        }

        // Handle checkbox columns with "in" operator
        if (
          "inputType" in column &&
          column.inputType === "checkbox" &&
          column.operator === "in"
        ) {
          if (column.optionValues) {
            return {
              ...column,
              template: makeTemplateField(
                EnumCheckboxField,
                queryBuilderComponentRef,
                column.field,
                {
                  optionValues: column.optionValues.map((value) => ({
                    label: value,
                    value,
                  })),
                  showSelectAll: true,
                } as EnumCheckboxFieldProps
              ),
            };
          }

          return {
            ...column,
            template: makeTemplateField(
              ODataCheckboxField,
              queryBuilderComponentRef,
              column.field,
              {
                dataManager: dataSource,
                queryBuilderComponentRef: queryBuilderComponentRef,
                fieldName: column.field,
                pageSize: 20,
                enableSearch: true,
                searchPlaceholder: `Search ${column.label?.toLowerCase()}...`,
                showMoreButtonText: "Load More",
                sortBy: column.field,
                sortOrder: "ascending",
                showSelectAll: true,
              } as ODataCheckboxFieldProps
            ),
          };
        }

        return column;
      }),
    [columns, dataSource]
  );

  const rule: RuleModel | undefined = useMemo(() => {
    if (value) {
      return value;
    }

    // todo harden this logic
    const isFirstColumnSimple = columns.length > 0 && "inputType" in columns[0];
    if (isFirstColumnSimple) {
      const castColumns = columns as SimpleFilterField[];

      return {
        condition: "and",
        rules: castColumns.map((column) => {
          return {
            field: column.field,
            operator: column.operator,
            value: column.value ?? (column.operator === "in" ? [null] : null),
            label: column.label,
            type: column.type,
          };
        }),
      } as RuleModel;
    }

    return undefined;
  }, [value, columns]);

  const clearFilters = useCallback(() => {
    if (queryBuilderComponentRef.current && rule) {
      queryBuilderComponentRef.current.rule = rule;

      // Trigger the rule change to notify parent components
      ruleChange(
        {
          rule,
          preventDefault: () => {},
        } as RuleChangeEventArgs,
        true
      );
    }
  }, [ruleChange, rule]);

  const isQueryBuilderStatePotentiallyDifferentThanDefault = useMemo(() => {
    return hasAtLeastOneRuleChangeBeenCalledSinceLastDefaultValueWasSet;
  }, [hasAtLeastOneRuleChangeBeenCalledSinceLastDefaultValueWasSet]);

  return {
    queryBuilderComponentRef,
    queryBuilderComponentKey,
    ruleChange,
    queryBuilderColumns,
    rule,
    clearFilters,
    isQueryBuilderStatePotentiallyDifferentThanDefault,
  };
};
